import { BfWebsocketEvent } from "@/lib/api/models";
import { websocketUrl } from "@/lib/client";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import useWebSocket, { ReadyState } from "react-use-websocket-lite";
import { useAppContext } from "./AppContext";

export enum WebsocketStatus {
    Connected,
    Connecting,
    Closed,
    Unauthorised,
}

const MAX_RECENT_MESSAGES = 100;

type SubscriberFn = (msg: BfWebsocketEvent) => void;

type WebsocketContextValue = {
    connectionStatus: WebsocketStatus;
    lastJsonMessage: BfWebsocketEvent | null;
    recentMessages: BfWebsocketEvent[];
    count: number;
    /** Subscribe to messages outside the React tree — does not cause a re-render
     *  on every message. Returns an unsubscribe function. */
    subscribe: (fn: SubscriberFn) => () => void;
};

const WebsocketContext = createContext<WebsocketContextValue | null>(null);

interface WebsocketProviderProps {
    children: React.ReactNode;
}

export const WebsocketProvider = ({ children }: WebsocketProviderProps) => {
    const { token } = useAppContext();

    const [lastJsonMessage, setLastJsonMessage] = useState<BfWebsocketEvent | null>(null);
    const [recentMessages, setRecentMessages] = useState<BfWebsocketEvent[]>([]);
    const [count, setCount] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState<WebsocketStatus>(
        WebsocketStatus.Connecting
    );

    const subscribersRef = useRef<Set<SubscriberFn>>(new Set());

    // Stable URL function: identity only changes when `token` changes, so the
    // socket reconnects on auth changes but stays put otherwise.
    const urlFn = useCallback((): string | null => {
        if (!token) return null;
        const base = websocketUrl();
        const params = new URLSearchParams({ token, json: "true" });
        return `${base}/v1/websocket/stream?${params.toString()}`;
    }, [token]);

    const handleMessage = useCallback((event: MessageEvent) => {
        if (typeof event.data !== "string") return;

        let parsed: BfWebsocketEvent;
        try {
            parsed = JSON.parse(event.data) as BfWebsocketEvent;
        } catch {
            return;
        }

        setLastJsonMessage(parsed);
        setRecentMessages((prev) => {
            const next = [parsed, ...prev];
            if (next.length > MAX_RECENT_MESSAGES) next.length = MAX_RECENT_MESSAGES;
            return next;
        });
        setCount((c) => c + 1);

        subscribersRef.current.forEach((fn) => fn(parsed));
    }, []);

    const { readyState } = useWebSocket({
        url: urlFn,
        onMessage: handleMessage,
        retryOnError: token !== undefined,
        shouldReconnect: () => token !== undefined,
        reconnectInterval: 20000,
        onError: (event: Event) => console.warn("Error connecting to event stream", event),
    });

    useEffect(() => {
        let next: WebsocketStatus = WebsocketStatus.Connecting;
        if (readyState === ReadyState.OPEN) next = WebsocketStatus.Connected;
        else if (readyState === ReadyState.CLOSED) next = WebsocketStatus.Closed;
        setConnectionStatus((prev) => (prev !== next ? next : prev));
    }, [readyState]);

    const subscribe = useCallback<WebsocketContextValue["subscribe"]>((fn) => {
        subscribersRef.current.add(fn);
        return () => {
            subscribersRef.current.delete(fn);
        };
    }, []);

    const value = useMemo<WebsocketContextValue>(
        () => ({ connectionStatus, lastJsonMessage, recentMessages, count, subscribe }),
        [connectionStatus, lastJsonMessage, recentMessages, count, subscribe]
    );

    return <WebsocketContext.Provider value={value}>{children}</WebsocketContext.Provider>;
};

export const useWebsocketContext = (): WebsocketContextValue => {
    const ctx = useContext(WebsocketContext);
    if (!ctx) {
        throw new Error("useWebsocketContext must be used within a WebsocketProvider");
    }
    return ctx;
};
